import { withSentry } from "@/lib/sentry-browser";

export type BufferedBrowserError = {
  error: unknown;
  mechanism: "auto.browser.global_handlers.onerror" | "auto.browser.global_handlers.onunhandledrejection";
  location?: { filename: string; lineno?: number; colno?: number };
};

/** Preserve script provenance, never credentials, arbitrary document paths or inline source. */
function safeFilename(filename: string): string {
  if (!filename) return "[unknown]";
  try {
    const url = new URL(filename, window.location.origin);
    if (url.protocol === "chrome-extension:" || url.protocol === "safari-web-extension:" || url.protocol === "moz-extension:") {
      return "[extension]";
    }
    if (url.origin === window.location.origin) {
      if (/^\/assets\/[\w./-]+\.m?js$/.test(url.pathname) || /^\/(?:login|notes|agent|subscribe)?\/?$/.test(url.pathname)) {
        return url.pathname;
      }
      return "[same-origin]";
    }
  } catch {
    // Inline/eval locations can contain source text rather than a URL.
  }
  return "[external-or-inline]";
}

export function bufferBrowserError(event: ErrorEvent): BufferedBrowserError {
  return {
    error: event.error ?? event.message,
    mechanism: "auto.browser.global_handlers.onerror",
    location: {
      filename: safeFilename(event.filename),
      ...(Number.isSafeInteger(event.lineno) && event.lineno > 0 ? { lineno: event.lineno } : {}),
      ...(Number.isSafeInteger(event.colno) && event.colno > 0 ? { colno: event.colno } : {}),
    },
  };
}

/** Low-cardinality diagnostics without recording the raw user agent. */
export function browserDiagnosticTags() {
  const agent = navigator.userAgent;
  const browsers = [
    ["edge", /Edg(?:e|A|iOS)?\/(\d+)/],
    ["chrome-ios", /CriOS\/(\d+)/],
    ["chrome", /Chrome\/(\d+)/],
    ["firefox", /(?:Firefox|FxiOS)\/(\d+)/],
    ["safari", /Version\/(\d+).*Safari\//],
  ] as const;
  for (const [family, pattern] of browsers) {
    const match = agent.match(pattern);
    if (match) return { "browser.family": family, "browser.major": match[1] };
  }
  return { "browser.family": "other" };
}

/** React's message/cause can contain DOM text and URL arguments. Do not forward either. */
export function reportRecoverableHydrationError(error: unknown, info: { componentStack?: string }) {
  const message = error instanceof Error ? error.message : "";
  const code = /^Minified React error #(\d+);/.exec(message)?.[1]
    ?? (/^(?:Hydration failed|Text content does not match server-rendered HTML)/.test(message) ? "hydration-mismatch" : "unknown");
  // Keep only component identifiers, not locations, props or development HTML diffs.
  const components = (info.componentStack ?? "").split("\n").flatMap((line) => {
    const name = /^\s+at ([A-Za-z_$][\w.$]*)(?:\s|\(|@|$)/.exec(line)?.[1];
    return name ? [name.slice(0, 80)] : [];
  }).slice(0, 30);
  const tags = browserDiagnosticTags();
  const document = safeFilename(window.location.href);
  withSentry((sentry) => sentry.captureEvent({
    level: "error",
    fingerprint: ["atlas-react-recoverable", code, components[0] ?? "unknown"],
    exception: { values: [{
      type: "ReactRecoverableError",
      value: "React root recovered from a rendering error",
      mechanism: { type: "auto.browser.react.on_recoverable_error", handled: true },
    }] },
    tags: { ...tags, "telemetry.component": "hydration", "react.error_code": code },
    contexts: { hydration: { components, document } },
  }));
}
